import { getToken } from "services/auth/tokenAuth";
import doRequest from "./client";
import { getUserId } from "services/storage/general";
import { DAY_IN_MILLISECONDS, EMPLOYABILITY_ENDPOINTS } from "./constants";
import {
    mapAdvisorySearchRequest,
    mapAdministrativeScheduleToUI,
    mapBaseResponse,
    mapCreateAdvisoryRequest
} from "./mappers";

export const requestGetEmploymentAdvisers = async () => {
    const endpoint = `${EMPLOYABILITY_ENDPOINTS.ADVISORS}?codigoUsuario=${encodeURIComponent(getUserId())}`;
    const [result, err] = await doRequest(endpoint, "GET", null, getToken());
    if (err) return null;
    const response = mapBaseResponse(result);
    if (!response.success) return null;
    return (response.data?.items || []).map(adviser => ({
        code: adviser.codigoAsesor,
        name: adviser.nombreCompleto ||
            [adviser.nombres, adviser.apellidos].filter(Boolean).join(" ")
    }));
};

export const requestCreateAdvisory = async form => {
    const body = {
        ...mapCreateAdvisoryRequest(form),
        codigoUsuario: getUserId()
    };
    if (!body.idCampusDetalleBase) {
        return {
            success: false,
            message: "EMP_CAMPUS_BASE_REQUERIDO"
        };
    }
    const [result, err] = await doRequest(
        EMPLOYABILITY_ENDPOINTS.ADVISORIES,
        "POST",
        body,
        getToken()
    );
    if (err) return { success: false, message: null };
    const response = mapBaseResponse(result);
    return {
        success: response.success,
        message: response.data?.responseCode || response.data?.descripcion || response.message
    };
};

export const requestSearchAdvisories = async filters => {
    const body = mapAdvisorySearchRequest({
        ...filters,
        userCode: getUserId()
    });
    const [result, err] = await doRequest(
        EMPLOYABILITY_ENDPOINTS.ADVISORIES_SEARCH,
        "POST",
        body,
        getToken()
    );
    if (err) return null;
    const response = mapBaseResponse(result);
    return response.success ? response.data?.items || [] : null;
};

export const requestGetAdvisorySlots = async (idAsesoriaFecha, studentCode = null) => {
    const [result, err] = await doRequest(
        EMPLOYABILITY_ENDPOINTS.ADVISORY_SLOTS(idAsesoriaFecha),
        "POST",
        {
            codigoUsuario: getUserId(),
            codigoAlumno: studentCode || null
        },
        getToken()
    );
    if (err) return null;
    const response = mapBaseResponse(result);
    return response.success ? response.data?.schedules || [] : null;
};

export const requestCreateInactivity = async ({
    durationId,
    date,
    timeStart,
    timeEnd,
    reason
}) => {
    const [result, err] = await doRequest(
        EMPLOYABILITY_ENDPOINTS.INACTIVITIES,
        "POST",
        {
            idDuracion: durationId,
            fecha: date,
            horaInicio: timeStart,
            horaFin: timeEnd,
            motivo: reason,
            codigoUsuario: getUserId()
        },
        getToken()
    );
    if (err) return false;
    return mapBaseResponse(result).success;
};

export const requestRestoreInactivity = async inactivityId => {
    const [result, err] = await doRequest(
        EMPLOYABILITY_ENDPOINTS.RESTORE_INACTIVITY(inactivityId),
        "POST",
        { codigoUsuario: getUserId() },
        getToken()
    );
    if (err) return false;
    return mapBaseResponse(result).success;
};

const requestAdministrativeSchedule = async ({
    adviserCode,
    date,
    studentCode
}) => {
    const dateFrom = date;
    const dateTo = adviserCode ? date + (6 * DAY_IN_MILLISECONDS) : date;
    const advisories = await requestSearchAdvisories({
        adviserCode,
        dateFrom,
        dateTo
    });
    if (advisories === null) return null;

    const slots = await Promise.all(advisories.map(async advisory => ({
        id: advisory.idAsesoriaFecha,
        items: await requestGetAdvisorySlots(advisory.idAsesoriaFecha, studentCode)
    })));
    if (slots.some(item => item.items === null)) return null;

    let schedules = mapAdministrativeScheduleToUI({
        advisories,
        slotsByAdvisoryDate: Object.fromEntries(slots.map(item => [item.id, item.items]))
    });
    if (adviserCode) {
        schedules = schedules.flatMap(schedule => {
            const appointmentsByDate = new Map();
            schedule.appointments.forEach(appointment => {
                const appointmentDate = appointment.schedule.date;
                const appointments = appointmentsByDate.get(appointmentDate) || [];
                appointments.push(appointment);
                appointmentsByDate.set(appointmentDate, appointments);
            });
            return Array.from(appointmentsByDate.values()).map(appointments => ({
                adviser: schedule.adviser,
                appointments
            }));
        });
    }
    return schedules;
};

export const requestGetAppointmentsByAllAdvisers = async ({ date }) =>
    requestAdministrativeSchedule({ date });

export const requestGetAppointmentsWeekByAdviser = async ({ adviserCode, date }) =>
    requestAdministrativeSchedule({ adviserCode, date });

export const requestGetAppointmentsForAdviser = async ({ studentId, date, adviserCode }) =>
    requestAdministrativeSchedule({
        adviserCode,
        date,
        studentCode: studentId
    });

export const requestUserHasAssociatedAppointments = async () => {
    const advisories = await requestSearchAdvisories({});
    return advisories === null ? null : advisories.length > 0;
};

export const requestGetScheduledAdvisers = async () => {
    const advisories = await requestSearchAdvisories({});
    if (advisories === null) return null;
    const advisers = new Map();
    advisories.forEach(advisory => {
        advisers.set(advisory.codigoAsesor, {
            code: advisory.codigoAsesor,
            name: [advisory.nombresAsesor, advisory.apellidosAsesor]
                .filter(Boolean)
                .join(" ")
        });
    });
    return Array.from(advisers.values());
};

export const requestCreateAppointment = requestCreateAdvisory;
